// Shared by Cloud Functions and the frontend (imported there as @shared).
// The scouting report on an opponent: what it runs and how it has done
// against what others ran. Public information only (every game's
// strategies show on its game page).
//
// team.scoutLog (written after each game by playDay, reset each season):
//   {year,
//    ran: {offense: {key: games}, defense: {...}, pace: {...},
//      keyed: games it keyed on a player},
//    last: [{g, d, o, w, s, os, ran: {offense, defense, pace, keyOn,
//      keyOnName}, faced: {...}}]  newest first,
//    vs: {defense: {key: {g, w, pts, poss}}   its scoring vs each defense
//         offense: {key: {g, w, pts, poss}}   points allowed vs each offense
//         keyed: {g, w, pts, poss}}}          its scoring when keyed on

import {possessions} from "./teamStats.js";

export const SCOUT_LAST = 10;

/**
 * @param {object} s strategy (may carry keyOn)
 * @param {object} [roster] the keyed team's players, for the name
 * @return {object} {offense, defense, pace, keyOn, keyOnName}
 */
function brief(s, roster) {
  const p = s?.keyOn ? roster?.[s.keyOn] : null;
  return {offense: s?.offense ?? null, defense: s?.defense ?? null,
    pace: s?.pace ?? null, keyOn: s?.keyOn ?? null,
    keyOnName: p ? `${p.fName ?? ""} ${p.lName ?? ""}`.trim() : null};
}

/**
 * @param {object|undefined} line {g, w, pts, poss}
 * @param {boolean} won
 * @param {number} pts
 * @param {number} poss
 * @return {object}
 */
function addLine(line, won, pts, poss) {
  return {g: (line?.g ?? 0) + 1, w: (line?.w ?? 0) + (won ? 1 : 0),
    pts: (line?.pts ?? 0) + pts,
    poss: Math.round(((line?.poss ?? 0) + poss) * 10) / 10};
}

/**
 * Adds one game to a team's scouting log. Returns a new log; a log from
 * an earlier season starts over.
 * @param {object|undefined} log team.scoutLog
 * @param {object} args
 * @param {number} args.year
 * @param {string} args.gameId
 * @param {number} args.day
 * @param {object} args.side the team's engine result (score, totals,
 *   strategy)
 * @param {object} args.opponent the other side's
 * @param {object} [args.players] this team's roster (names of players
 *   the other team keyed on)
 * @param {object} [args.oppPlayers] the opponent's roster
 * @return {object}
 */
export function addScoutGame(log, {year, gameId, day, side, opponent,
  players, oppPlayers}) {
  const base = log?.year === year ? log : {year, ran: {}, last: [], vs: {}};
  const won = side.score > opponent.score;
  const ran = {};
  for (const cat of ["offense", "defense", "pace"]) {
    const key = side.strategy?.[cat];
    ran[cat] = {...base.ran?.[cat]};
    if (key) ran[cat][key] = (ran[cat][key] ?? 0) + 1;
  }
  ran.keyed = (base.ran?.keyed ?? 0) + (side.strategy?.keyOn ? 1 : 0);
  const ownPoss = possessions(side.totals);
  const oppPoss = possessions(opponent.totals);
  const vs = {
    defense: {...base.vs?.defense}, offense: {...base.vs?.offense},
    ...(base.vs?.keyed ? {keyed: base.vs.keyed} : {}),
  };
  const theirs = opponent.strategy ?? {};
  if (theirs.defense) {
    vs.defense[theirs.defense] = addLine(vs.defense[theirs.defense], won,
        side.score, ownPoss);
  }
  if (theirs.offense) {
    vs.offense[theirs.offense] = addLine(vs.offense[theirs.offense], won,
        opponent.score, oppPoss);
  }
  if (theirs.keyOn) vs.keyed = addLine(vs.keyed, won, side.score, ownPoss);
  const entry = {g: gameId, d: day, o: opponent.id, w: won, s: side.score,
    os: opponent.score, ran: brief(side.strategy, oppPlayers),
    faced: brief(theirs, players)};
  return {year, ran, last: [entry, ...(base.last ?? [])].slice(0, SCOUT_LAST),
    vs};
}

/**
 * Points per 100 possessions of a vs line.
 * @param {object} line {pts, poss}
 * @return {number|null}
 */
export function per100(line) {
  return line?.poss ? 100 * line.pts / line.poss : null;
}

/**
 * Each player's share of the team's shots and points this season, highest
 * scorers first (players who have played).
 * @param {object} team team document
 * @param {number} [count]
 * @return {object[]} [{id, name, pos1, gp, ppg, ptsShare, fgaShare,
 *   tpaShare (share of own shots that are threes)}]
 */
export function shotTakers(team, count = 5) {
  const list = Object.values(team.players ?? {})
      .filter((p) => (p.season?.gp ?? 0) > 0);
  const total = (k) => list.reduce((s, p) => s + (p.season?.[k] ?? 0), 0) ||
    1;
  const pts = total("pts");
  const fga = total("fga");
  return list.map((p) => ({
    id: p.id, name: `${p.fName} ${p.lName}`, pos1: p.pos1,
    injured: p.injury?.gamesOut > 0, gp: p.season.gp,
    ppg: p.season.pts / p.season.gp,
    ptsShare: p.season.pts / pts,
    fgaShare: (p.season.fga ?? 0) / fga,
    tpaShare: p.season.fga ? (p.season.tpa ?? 0) / p.season.fga : 0,
  })).sort((a, b) => b.ptsShare - a.ptsShare).slice(0, count);
}
